// Day Counter Widget - Track PTO, Sick Days, or any day-based counters
import React from 'react';
import { Plus, Minus, Calendar } from 'lucide-react';

interface DayCounter {
  label: string;
  used: number;
  total: number;
  color: string; // e.g., 'blue', 'red', 'green'
}

interface DayCounterWidgetProps {
  counters: DayCounter[];
  onUpdate: (index: number, used: number) => void;
}

export default function DayCounterWidget({ counters, onUpdate }: DayCounterWidgetProps) {
  const getColorClasses = (color: string) => {
    const colors: Record<string, { bg: string; text: string; bar: string; border: string }> = {
      blue: {
        bg: 'bg-blue-500/10',
        text: 'text-blue-300',
        bar: 'bg-blue-500',
        border: 'border-blue-500/30'
      },
      red: {
        bg: 'bg-red-500/10',
        text: 'text-red-300',
        bar: 'bg-red-500',
        border: 'border-red-500/30'
      },
      green: {
        bg: 'bg-green-500/10',
        text: 'text-green-300',
        bar: 'bg-green-500',
        border: 'border-green-500/30'
      },
      amber: {
        bg: 'bg-amber-500/10',
        text: 'text-amber-300',
        bar: 'bg-amber-500',
        border: 'border-amber-500/30'
      }
    };
    return colors[color] || colors.blue;
  };

  return (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mb-4">
      {counters.map((counter, index) => {
        const colors = getColorClasses(counter.color);
        const percentage = (counter.used / counter.total) * 100;
        const remaining = counter.total - counter.used;

        return (
          <div
            key={index}
            className={`rounded-lg border ${colors.border} ${colors.bg} p-3 backdrop-blur-sm`}
          >
            {/* Header */}
            <div className="flex items-center justify-between mb-2">
              <div className="flex items-center gap-2">
                <Calendar className={`w-4 h-4 ${colors.text}`} />
                <span className={`text-sm font-semibold ${colors.text}`}>
                  {counter.label}
                </span>
              </div>
              <span className="text-xs text-slate-400">
                {remaining} left
              </span>
            </div>

            {/* Counter Display */}
            <div className="flex items-center justify-between mb-2">
              <div className="flex items-center gap-2">
                <button
                  onClick={() => onUpdate(index, Math.max(0, counter.used - 1))}
                  className="p-1 rounded bg-slate-700/50 hover:bg-slate-700 text-slate-300 hover:text-white transition-colors"
                  disabled={counter.used === 0}
                >
                  <Minus className="w-3 h-3" />
                </button>
                <span className={`text-2xl font-bold ${colors.text} min-w-[60px] text-center`}>
                  {counter.used} / {counter.total}
                </span>
                <button
                  onClick={() => onUpdate(index, Math.min(counter.total, counter.used + 1))}
                  className="p-1 rounded bg-slate-700/50 hover:bg-slate-700 text-slate-300 hover:text-white transition-colors"
                  disabled={counter.used >= counter.total}
                >
                  <Plus className="w-3 h-3" />
                </button>
              </div>
            </div>

            {/* Progress Bar */}
            <div className="w-full bg-slate-700/30 rounded-full h-2 overflow-hidden">
              <div
                className={`${colors.bar} h-full transition-all duration-300 ease-out`}
                style={{ width: `${percentage}%` }}
              />
            </div>

            {/* Percentage */}
            <div className="text-right mt-1">
              <span className="text-xs text-slate-400">
                {percentage.toFixed(0)}% used
              </span>
            </div>
          </div>
        );
      })}
    </div>
  );
}
